import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { renderIf } from "MODULES/shared/decorators";
import { stringify } from "query-string";
import api from "APP_ROOT/api";
import { toastr } from "react-redux-toastr";
import { matchesBoolean, matchesString } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { hasRole } from "MODULES/users/helpers";
import { ADMIN } from "MODULES/users/constants";
import { Button, Search } from "MODULES/shared/components";
import {
  API_BASE,
  CLEAR_FILTERS_TEXT,
  NOT_RECEIVED,
  RECEIVED
} from "APP_ROOT/constants";
import {
  REPAIR_EVALUATION_APPROVED,
  REPAIR_EVALUATION_RECEIVED,
  WORKING_TO_SAVE_UNIT,
  REPAIR_EVALUATION_REJECTED,
  REPAIR_AWAITING_EVALUATION,
  AWAITING_TO_SHIP
} from "MODULES/RepairOrders/constants";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { STORE_OWNER_ID } from "MODULES/inventories/constants";
import { properCase } from "LIB/formatters/text";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import Table from "./Table";
import { repairOrdersPagination } from "../../pagination";

const defaultFilters = {
  approval_status: undefined,
  awaiting_to_ship: undefined,
  eval_received: undefined,
  expected_date_past_due: undefined,
  evaluation_fees_exist: undefined,
  exchange_gone_outright: undefined,
  final_approval: undefined,
  no_returning_awb: undefined,
  open: undefined,
  my_ros: undefined,
  not_approved: undefined,
  po_not_paid: undefined,
  received: undefined,
  repair_and_hold: undefined,
  invoiced: undefined,
  invoice_unpaid: undefined,
  managed_repairs: undefined,
  open_cores: undefined,
  payables_pending: undefined,
  store_owner: undefined,
  shipped: undefined,
  action_requested: undefined,
  no_listed_inventory: undefined,
  no_work_performed: undefined,
  repair_awaiting_evaluation: undefined,
  repair_evaluation_approved: undefined,
  repair_evaluation_received: undefined,
  repair_evaluation_rejected: undefined,
  store_owner_id: undefined,
  ro_greater_than_zero: undefined,
  teardown: undefined,
  shop_assist: undefined,
  invoice_due_date_within_30_days: undefined,
  not_purchased_as_is: undefined,
  working_to_save_unit: undefined
};

const filters = [
  {
    label: "Open ROs",
    filters: {
      payables_pending: undefined,
      open: true
    }
  },
  {
    label: "Open SOs",
    filters: {
      open_sales_orders: true,
      received: false
    }
  },
  {
    label: "Awaiting RS Approval",
    filters: {
      not_approved: true,
      payables_pending: undefined
    }
  },
  {
    label: "No Listed Inventory",
    filters: {
      no_listed_inventory: true,
      payables_pending: undefined,
      received: false
    }
  },
  {
    label: "Vendor Payables Pending",
    filters: {
      invoice_due_date_within_30_days: true,
      not_purchased_as_is: true,
      payables_pending: true,
      po_not_paid: true,
      open: true,
      received: false,
      repair_evaluation_received: true,
      repair_awaiting_evaluation: true,
      shipped: true
    },
    hr: true
  },
  {
    label: "My ROs",
    filters: {
      payables_pending: undefined,
      my_ros: true
    }
  },
  {
    label: "Awaiting To Ship To Shop",
    filters: {
      awaiting_to_ship: true,
      shipped: false,
      received: false
    }
  },
  {
    label: "Awaiting Evaluation",
    filters: {
      repair_awaiting_evaluation: true,
      payables_pending: undefined,
      received: false,
      shipped: true
    }
  },
  {
    label: "Original Eval Entered",
    filters: {
      eval_received: true,
      payables_pending: undefined
    }
  },
  {
    label: "Evaluation Received",
    filters: {
      repair_evaluation_received: true,
      payables_pending: undefined,
      shipped: true,
      received: false
    }
  },
  {
    label: "Evaluation Approved",
    filters: {
      repair_evaluation_approved: true,
      payables_pending: undefined,
      shipped: true,
      received: false
    }
  },
  {
    label: "Evaluation Rejected",
    filters: {
      repair_evaluation_rejected: true,
      payables_pending: undefined,
      received: false,
      shipped: true
    }
  },
  {
    label: "No Work Performed",
    filters: {
      no_work_performed: true,
      shipped: true
    }
  },
  {
    label: "Evaluation Fees",
    filters: {
      evaluation_fees_exist: true
    }
  },
  {
    label: "Missing Original Eval",
    filters: {
      eval_received: false,
      payables_pending: undefined
    },
    hr: true
  },
  {
    label: properCase(RECEIVED),
    filters: {
      received: true
    }
  },
  {
    label: properCase(NOT_RECEIVED),
    filters: {
      received: false
    }
  },
  {
    label: "Received and Not Invoiced",
    filters: {
      payables_pending: undefined,
      received: true,
      invoiced: false
    }
  },
  {
    label: "Received and Invoiced",
    filters: {
      payables_pending: undefined,
      received: true,
      invoiced: true
    }
  },
  {
    label: "Invoice Not Paid / Remaining Balance",
    filters: {
      invoice_unpaid: true
    }
  },
  {
    label: "Synced with Accounting",
    filters: {
      accounting_synced: true
    }
  },
  {
    label: "Not Synced with Accounting",
    filters: {
      accounting_synced: false
    }
  },
  {
    label: "Returning From Shop",
    filters: {
      payables_pending: undefined,
      returning_from_shop: true,
      shipped: true,
      received: false
    }
  },
  {
    label: "Missing Returning AWB",
    filters: {
      no_returning_awb: true,
      payables_pending: undefined,
      received: false,
      shipped: true
    }
  },
  {
    label: "Ex Date Past Due",
    filters: {
      expected_date_past_due: true
    },
    hr: true
  },
  {
    label: "Exchange Core Returns",
    filters: {
      cores: true,
      payables_pending: undefined
    }
  },
  {
    label: "Exchange Gone Outright",
    filters: {
      exchange_gone_outright: true
    }
  },
  {
    label: "Warranty Considerations",
    filters: {
      action_requested: "warranty_consideration",
      payables_pending: undefined
    }
  },
  {
    label: "Working to Save Unit",
    filters: {
      working_to_save_unit: true,
      payables_pending: undefined,
      received: false,
      shipped: true
    }
  },
  {
    label: "Repair & Hold",
    filters: {
      no_returning_awb: true,
      payables_pending: undefined,
      repair_and_hold: true,
      shipped: true,
      received: false
    }
  },
  {
    label: "Not on Repair & Hold",
    filters: {
      payables_pending: undefined,
      received: false,
      repair_and_hold: false,
      shipped: true
    }
  },
  {
    label: "Internal Outrights",
    filters: {
      internal_outright: true,
      payables_pending: undefined
    }
  },
  {
    label: "Second Opinions",
    filters: { payables_pending: undefined, for_second_opinion: true }
  },
  {
    label: "Managed Repairs",
    filters: {
      managed_repairs: true
    }
  },
  {
    label: "Awaiting Scrap Report",
    filters: {
      awaiting_scrap: true,
      payables_pending: undefined,
      received: false,
      shipped: true
    }
  },
  {
    label: "ROs Greater than $0",
    filters: {
      ro_greater_than_zero: true
    }
  },
  {
    label: "Teardowns",
    filters: {
      teardown: true
    }
  },
  {
    label: "Shop Assist",
    filters: {
      shop_assist: true
    }
  }
];

const filterTags = [
  {
    label: "Accounting Synced",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Synced w/ Accounting",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Show Open ROs",
    key: "open",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Listed Inventory",
    key: "no_listed_inventory"
  },
  {
    label: "Received & Invoiced",
    key: "received_and_invoiced"
  },
  {
    label: "Invoice Not Paid / Remaining Balance",
    key: "invoice_unpaid",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Internal Outrights",
    key: "internal_outright"
  },
  {
    label: "Received",
    key: "received",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Received",
    key: "received",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Not R&H",
    key: "repair_and_hold",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Invoiced",
    key: "invoiced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Ex Date Past Due",
    key: "expected_date_past_due",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Invoiced",
    key: "invoiced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "No Work Performed",
    key: "no_work_performed"
  },
  {
    label: "Core Returns",
    key: "open_cores"
  },
  {
    label: "Core Returns",
    key: "cores"
  },
  {
    label: "Open SOs",
    key: "open_sales_orders"
  },
  {
    label: "Orig. Eval Received",
    key: "eval_received",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Missing Orig. Eval",
    key: "eval_received",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Shipped",
    key: "shipped"
  },
  {
    label: "Awaiting To Ship",
    key: AWAITING_TO_SHIP
  },
  {
    label: "Awaiting Eval.",
    key: REPAIR_AWAITING_EVALUATION
  },
  {
    label: "Eval. Rejected",
    key: REPAIR_EVALUATION_REJECTED
  },
  {
    label: "Working to Save Unit",
    key: WORKING_TO_SAVE_UNIT
  },
  {
    label: "Eval. Approved",
    key: REPAIR_EVALUATION_APPROVED
  },
  {
    label: "Eval. Received",
    key: REPAIR_EVALUATION_RECEIVED
  },
  {
    label: "No Returning AWB",
    key: "no_returning_awb"
  },
  {
    label: "Repair & Hold",
    key: "repair_and_hold",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Evaluation Fees",
    key: "evaluation_fees_exist",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Awaiting Scrap",
    key: "awaiting_scrap"
  },
  {
    label: "Awaiting RS Approval",
    key: "not_approved",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Returning from Shop",
    key: "returning_from_shop"
  },
  {
    label: "Shipped To Shop",
    key: "approval_status_not",
    matchesTarget: matchesString(AWAITING_TO_SHIP)
  },
  {
    label: "My ROs",
    key: "my_ros"
  },
  {
    label: "POs Not Paid",
    key: "po_not_paid"
  },
  {
    label: "Final Approval",
    key: "final_approval",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Warranty Consideration",
    key: "action_requested",
    matchesTarget: matchesString("warranty_consideration")
  },
  {
    label: "Not Purchased As-Is",
    key: "not_purchased_as_is",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Eval. Not Rejected",
    key: "approval_status_not",
    matchesTarget: matchesString(REPAIR_EVALUATION_REJECTED)
  },
  {
    label: "Invoice Due Date w/i 30 days",
    key: "invoice_due_date_within_30_days",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Second Opinions",
    key: "for_second_opinion",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Managed Repairs",
    key: "managed_repairs"
  },
  {
    label: "ROs Greater than $0",
    key: "ro_greater_than_zero",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Teardowns",
    key: "teardown",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Shop Assist",
    key: "shop_assist",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Exchange Gone Outright",
    key: "exchange_gone_outright",
    matchesTarget: matchesBoolean(true)
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const total = searchProps.paginator.get("totalCount");

  params.total_count = total;
  return (
    <Button
      onClick={() => {
        if (total > 200) {
          toastr.info("Processing Report.");
          api.repairOrders.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/repair_orders/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Repair Orders Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

ExportButton.propTypes = {
  searchProps: PropTypes.shape({
    paginator: PropTypes.instanceOf(Map)
  })
};

const meetsExportCriteria = renderIf(({ searchProps }) => {
  const user = useCurrentUser();
  return searchProps.paginator.get("totalCount") <= 450 || hasRole(user, ADMIN);
});

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function RepairOrderSearch({ pushHistory }) {
  const currentUser = useCurrentUser();
  const filterWithLocations = currentUser.locations.map(l => ({
    label: `Owned By ${l.name}`,
    filters: { store_owner_id: l.id }
  }));

  const filterTagsWithLocations = currentUser.locations.map(l => ({
    label: `Owned by ${l.name}`,
    key: STORE_OWNER_ID,
    matchesTarget: matchesString(l.id)
  }));

  return (
    <Search search={repairOrdersPagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a RO #, eval due date, vendor name, vendor #, invoice #, work order#, serial#, part#, buyer, created by or part description..."
                submitOnSingleResult={r =>
                  pushHistory(`/repair-orders/${r.repair_order_uuid}`)}
              />
              <DecoratedExportButton searchProps={searchProps} />
              <DateRangePicker {...searchProps} />
              <FilterSelect
                {...searchProps}
                filters={filters.concat(filterWithLocations).concat({
                  label: CLEAR_FILTERS_TEXT,
                  filters: defaultFilters
                })}
                right
              />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Repair Orders"
            filters={filterTagsWithLocations.concat(filterTags)}
            aggregateField="subtotal"
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

RepairOrderSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(RepairOrderSearch);



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/Search/RepairOrderSearch.jsx